import React, { useContext, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { ThemeContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { amber } from '@mui/material/colors';
import { ModalDateFieldProvider } from 'src/components/UI/input/ModalDateFieldContext';
import UnsavedChangeCount from 'src/components/UI/input/changed-field/UnsavedChangeCount';

// Shared width for clinical-record entry modals (Contact, Consent, Sample,
// Test, Treatment, Referral, Disease/genotype/phenotype, etc.). A little wider
// than the MUI 'sm' preset (600px) so the forms — and the wide merge-field
// picker — have more breathing room. Passed to CustomDialog as a numeric
// `maxWidth`; tune this one value to resize every clinical-record modal at once.
export const CLINICAL_RECORD_DIALOG_WIDTH = 850;

// `fullScreen` (TGCLOUD-1868, plan D2): the dialog fills the window, for a Test whose lab report
// is being checked beside it. Only the size changes: the header, its close button, what closes the
// dialog, ModalDateFieldProvider and the children are the same elements in the same places, so a
// dialog that goes full screen while it is open keeps everything in it. Its content area becomes a
// column the child can fill (`flex: 1`, `minHeight: 0`) and scroll inside itself; a child that does
// not is scrolled by the content area as before.
const FULL_SCREEN_CONTENT_SX = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  padding: 1.5
};

const StyledTitle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10
}));

const StyledDialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2)
}));

const StyledDialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const StyledDialogActions = styled(MuiDialogActions)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(1),
  gap: 15,
  justifyContent: 'flex-end'
}));

function hexToRGB(hex, alpha) {
  var r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
  } else {
    return 'rgb(' + r + ', ' + g + ', ' + b + ')';
  }
}

const DialogTitle = (props) => {
  const { children, onClose, appearance, titleStyle, ...other } = props;
  const { theme } = React.useContext(ThemeContext);
  const headerBg = theme.buttonStyle || theme.accent || '#f50057';
  const headerText = theme.buttonText || '#fff';
  const t = useAutoTranslation();

  const getTitleContent = () => {
    // Check if title is a React element or a string
    return typeof children === 'string' ? t(children) : children;
  };

  return (
    <StyledDialogTitle
      {...other}
      sx={{ backgroundColor: headerBg, color: headerText }}
    >
      <StyledTitle style={{ opacity: 1 }}>
        {appearance === 'warning' && (
          <WarningIcon fontSize="small" style={{ color: amber[500] }} />
        )}
        <Typography variant="h6" style={{ opacity: 1, ...titleStyle }}>
          {getTitleContent()}
        </Typography>
      </StyledTitle>
      {onClose ? (
        <IconButton
          aria-label={t('close')}
          onClick={onClose}
          size="large"
          sx={{ position: 'absolute', right: 8, top: 8, color: headerText }}
          style={{ opacity: 1 }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </StyledDialogTitle>
  );
};

export default function CustomDialog(props) {
  const { theme } = React.useContext(ThemeContext);
  const t = useAutoTranslation();

  const {
    open,
    title,
    handleClose,
    minHeight,
    titleAppearance,
    allowBackgroundClose,
    showCancelBtn,
    saveBtnLabel,
    distanceTop = 10,
    cancelBtnLabel,
    isParentHandlingLoading = false,
    isParentLoading = false,
    titleStyle = {},
    fullScreen = false
  } = props;

  const [loading, setLoading] = React.useState(false);
  const saveClickHandler = () => {
    setLoading(true);
    props
      .onSave()
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // This is used if we are handling loading state on parent comp
  useEffect(() => {
    if (isParentHandlingLoading) {
      setLoading(isParentLoading);
    }
  }, [isParentHandlingLoading, isParentLoading]);

  const CancelActionBtn = ({ label, handleClose }) => {
    return (
      <Button variant="outlined" onClick={handleClose}>
        {t(label) || t('Cancel')}
      </Button>
    );
  };

  const onClose = (e, reason) => {
    if (
      allowBackgroundClose ||
      (reason !== 'escapeKeyDown' && reason !== 'backdropClick')
    ) {
      return handleClose(e);
    }
  };

  const stopPropagationForTab = (event) => {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  };

  // maxWidth may be a MUI breakpoint key ('xs'|'sm'|'md'|'lg'|'xl') OR a raw
  // pixel number for a custom width (MUI's presets are coarse — e.g. the
  // clinical-record modals want ~850px, between 'sm' 600 and 'md' 900). For a
  // number we disable the breakpoint cap and set the paper max-width via sx so
  // it still cooperates with `fullWidth` (paper = min(100% - margins, width)).
  const rawMaxWidth = props.maxWidth || 'sm';
  const numericMaxWidth = typeof rawMaxWidth === 'number' ? rawMaxWidth : null;

  return (
    <Dialog
      onClose={onClose}
      onKeyDown={stopPropagationForTab}
      aria-labelledby="p-dialog"
      open={open}
      fullWidth
      fullScreen={fullScreen}
      maxWidth={numericMaxWidth ? false : rawMaxWidth}
      sx={
        numericMaxWidth && !fullScreen
          ? { '& .MuiDialog-paper': { maxWidth: numericMaxWidth } }
          : undefined
      }
    >
      <DialogTitle
        id="p-dialog"
        onClose={handleClose}
        appearance={titleAppearance}
        titleStyle={titleStyle}
      >
        {title}
      </DialogTitle>
      <StyledDialogContent
        dividers
        sx={fullScreen ? FULL_SCREEN_CONTENT_SX : undefined}
      >
        <ModalDateFieldProvider>
          {props.children ? (
            props.children
          ) : (
            <Typography gutterBottom>{t(props.content)}</Typography>
          )}
        </ModalDateFieldProvider>
      </StyledDialogContent>
      {props.showActions && (
        <StyledDialogActions>
          {/* TGCLOUD-1902: how many fields of a clinician's edit screen hold an
              unsaved change, and the way to them. Absent, nothing is drawn. */}
          {Number.isFinite(props.unsavedCount) && (
            <UnsavedChangeCount count={props.unsavedCount} />
          )}
          <Button
            onClick={isParentHandlingLoading ? props.onSave : saveClickHandler}
            color="secondary"
            variant="contained"
            // `saveDisabled` lets a caller gate its own primary action (e.g. a
            // required field not yet answered). Additive and absent by
            // default, so every existing modal behaves exactly as before.
            disabled={loading || props.saveDisabled === true}
            endIcon={loading ? <CircularProgress size={15} /> : undefined}
          >
            {t(saveBtnLabel) || t('Save')}
          </Button>
          <CancelActionBtn label={cancelBtnLabel} handleClose={handleClose} />
          {props.onSavePDFCopy && (
            <Button
              onClick={props.onSavePDFCopy}
              color="secondary"
              variant="contained"
            >
              {t('Save PDF Copy')}
            </Button>
          )}
        </StyledDialogActions>
      )}
      {showCancelBtn && (
        <StyledDialogActions>
          <CancelActionBtn handleClose={handleClose} label={saveBtnLabel} />
        </StyledDialogActions>
      )}
      {/* Optional caller-supplied bottom-right action (e.g. a record-history
          icon). Renders nothing unless provided, so existing usages are
          unaffected. */}
      {props.footerAction && (
        <StyledDialogActions>{props.footerAction}</StyledDialogActions>
      )}
    </Dialog>
  );
}
